const TYPES_FACTURES = new Set(['FACTURE', 'AVOIR']);
const TYPES_MARGE = new Set(['FACTURE', 'AVOIR', 'RETOUR', 'AUTRE_CORRECTION_CERTIFIEE']);

const texte = (valeur) => String(valeur || '').trim();
const commercialNormalise = (valeur) => texte(valeur).toLocaleLowerCase('fr-FR');
const nombre = (valeur) => {
  if (valeur == null || valeur === '') return null;
  const resultat = Number(valeur);
  return Number.isFinite(resultat) ? resultat : null;
};

const numeroCommande = (document) => texte(document?.donnees?.numero_commande || document?.commande_numero);
const numeroDocument = (document) => texte(
  document?.donnees?.numero_document
  || document?.document_numero
  || document?.donnees?.numero_facture
  || (TYPES_FACTURES.has(document?.type_document) ? document?.identifiant_source : '')
).toUpperCase();

const commandesParNumero = (documents = []) => new Map(documents
  .filter((document) => document?.type_document === 'COMMANDE')
  .map((document) => [texte(document?.donnees?.numero_commande || document?.identifiant_source), document])
  .filter(([numero]) => numero));

function dansPerimetre(document, commandes, codes, options = {}) {
  const donnees = document?.donnees || {};
  const codeClient = texte(donnees.code_client || document?.code_client).toUpperCase();
  const codesConnus = options.codesClientsConnus instanceof Set ? options.codesClientsConnus : codes;
  if (codes.size && (codesConnus.has(codeClient) ? !codes.has(codeClient) : options.inclureNonRapproches !== true)) return false;
  const commercialCible = commercialNormalise(options.commercialCommande);
  if (!commercialCible || ['tous', 'toute l’équipe', "toute l'équipe", "toute l'equipe", 'toute l’équipe commerciale'].includes(commercialCible)) return true;
  const commande = commandes.get(numeroCommande(document));
  const commercial = commercialNormalise(donnees.commercial_commande || document?.commercial_commande || commande?.donnees?.commercial_commande) || 'non attribué';
  return commercial === commercialCible;
}

function signer(type, valeur, sens) {
  if (valeur == null) return null;
  if (sens === -1 || texte(sens).toUpperCase() === 'NEGATIF') return -Math.abs(valeur);
  if (sens === 1 || texte(sens).toUpperCase() === 'POSITIF') return Math.abs(valeur);
  return ['AVOIR', 'RETOUR'].includes(type) ? -Math.abs(valeur) : valeur;
}

export function normaliserMouvementMarge(document, commandes = new Map()) {
  const donnees = document?.donnees || {};
  const type = texte(donnees.type_mouvement || document?.type_mouvement || document?.type_document).toUpperCase();
  if (!TYPES_MARGE.has(type)) return null;
  const margeBrute = nombre(donnees.marge_euros ?? donnees.marge ?? document?.marge_euros);
  const caHtBrut = nombre(donnees.ca_ht_mouvement ?? donnees.montant_ht ?? document?.ca_ht_mouvement);
  const caTtcBrut = nombre(donnees.ca_ttc_mouvement ?? donnees.montant_ttc ?? document?.ca_ttc_mouvement);
  const numero = numeroDocument(document);
  if (!numero || margeBrute == null || caHtBrut == null) return null;
  const commandeNumero = numeroCommande(document);
  const commande = commandes.get(commandeNumero);
  return {
    id: texte(document?.id || `${type}|${numero}`),
    typeMouvement: type,
    document: numero,
    numeroCommande: commandeNumero,
    date: texte(donnees.date_mouvement || document?.date_mouvement || donnees.date_facturation || donnees.date_creation).slice(0, 10),
    caHT: signer(type, caHtBrut, donnees.sens ?? document?.sens),
    caTTC: signer(type, caTtcBrut, donnees.sens ?? document?.sens),
    marge: signer(type, margeBrute, donnees.sens ?? document?.sens),
    codeClient: texte(donnees.code_client || document?.code_client).toUpperCase(),
    commercialCommande: texte(donnees.commercial_commande || document?.commercial_commande || commande?.donnees?.commercial_commande),
    source: texte(donnees.source_marge || document?.source_marge || document?.source || 'INTERCLUB'),
    certification: texte(donnees.certification || document?.certification || 'A_CONTROLER').toUpperCase(),
    lignes: donnees.lignes_marge || document?.lignes_marge || [],
    numeroFactureOrigine: texte(donnees.numero_facture_origine || document?.numero_facture_origine).toUpperCase(),
  };
}

export function mouvementsMargeFactureeInterclub(documents = [], codesClients = new Set(), options = {}) {
  const codes = codesClients instanceof Set ? codesClients : new Set(codesClients || []);
  const commandes = commandesParNumero(documents);
  const explicites = documents.filter((document) => document?.type_document === 'MARGE_FACTUREE');
  const sources = explicites.length ? explicites : documents.filter((document) => TYPES_MARGE.has(document?.type_document));
  const uniques = new Map();
  sources.forEach((document) => {
    const mouvement = normaliserMouvementMarge(document, commandes);
    if (!mouvement || !dansPerimetre(document, commandes, codes, options)) return;
    uniques.set(`${mouvement.typeMouvement}|${mouvement.document}`, mouvement);
  });
  return [...uniques.values()];
}

const clePiece = (type, numero) => `${texte(type).toUpperCase()}|${texte(numero).toUpperCase()}`;
const dansPeriode = (mouvement, debut, fin) => Boolean(mouvement?.date)
  && (!debut || mouvement.date >= debut)
  && (!fin || mouvement.date <= fin);

export function agregerMargeFacturee(mouvements = [], piecesCa = [], debut = '', fin = '') {
  const selection = mouvements.filter((mouvement) => dansPeriode(mouvement, debut, fin));
  const index = new Map(selection.map((mouvement) => [clePiece(mouvement.typeMouvement, mouvement.document), mouvement]));
  const pieces = piecesCa.filter((piece) => dansPeriode(piece, debut, fin));
  const nonCollectes = pieces.filter((piece) => !index.has(clePiece(piece.typeDocument, piece.identifiant)));
  const caHT = selection.reduce((total, mouvement) => total + Number(mouvement.caHT || 0), 0);
  const marge = selection.reduce((total, mouvement) => total + Number(mouvement.marge || 0), 0);
  const certifications = new Set(selection.map((mouvement) => mouvement.certification));
  const disponible = selection.length > 0;
  const complet = disponible && nonCollectes.length === 0;
  const cleCommande = (item, indexPiece) => texte(item?.numeroCommande || item?.commandeNumero || item?.donnees?.numero_commande || item?.numero_commande)
    || `${texte(item?.typeDocument || item?.typeMouvement)}|${texte(item?.identifiant || item?.document)}|${indexPiece}`;
  const commandesEligibles = new Set([...selection, ...nonCollectes].map(cleCommande).filter(Boolean));
  const commandesIncompletes = new Set(nonCollectes.map(cleCommande).filter(Boolean));
  const commandesCouvertes = new Set([...commandesEligibles].filter((commande) => !commandesIncompletes.has(commande)));
  const caHtTotalEligible = pieces.reduce((total, piece) => total + Number(piece?.montantHT || piece?.caHT || piece?.donnees?.montant_ht || 0), 0);
  return {
    montant: disponible ? marge : null,
    caHT: disponible ? caHT : null,
    taux: disponible && Math.abs(caHT) > 0.000001 ? marge / caHT * 100 : null,
    statut: !disponible ? 'MARGE NON COLLECTÉE' : !complet ? 'MARGE PARTIELLE' : (certifications.has('A_CONTROLER') || certifications.has('À CONTRÔLER') ? 'À CERTIFIER' : 'CERTIFIÉ'),
    complet,
    couverture: {
      commandesCouvertes: commandesCouvertes.size,
      commandesEligibles: commandesEligibles.size,
      pourcentage: commandesEligibles.size ? commandesCouvertes.size / commandesEligibles.size * 100 : 0,
      caHtCouvert: caHT,
      caHtTotalEligible: caHtTotalEligible || null,
    },
    mouvements: selection,
    nonCollectes,
    nombreNonCollecte: nonCollectes.length,
  };
}

export function mouvementsMargeEncaisseeInterclub(mouvementsFactures = [], paiements = []) {
  const factures = new Map(mouvementsFactures
    .filter((mouvement) => mouvement.typeMouvement === 'FACTURE')
    .map((mouvement) => [mouvement.document, mouvement]));
  const dejaAffecte = new Map();
  return [...paiements]
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || String(a.identifiant || '').localeCompare(String(b.identifiant || '')))
    .map((paiement) => {
      const facture = factures.get(texte(paiement.numeroFacture).toUpperCase());
      if (!facture || !Number.isFinite(Number(facture.caTTC)) || Math.abs(Number(facture.caTTC)) < 0.000001) return { ...paiement, marge: null, caHT: null, certification: 'MARGE NON COLLECTÉE' };
      const baseTtc = Math.abs(Number(facture.caTTC));
      const montant = Number(paiement.montantTTC || 0);
      const utilise = dejaAffecte.get(facture.document) || 0;
      const montantEligible = Math.sign(montant || 1) * Math.min(Math.abs(montant), Math.max(0, baseTtc - Math.abs(utilise)));
      dejaAffecte.set(facture.document, utilise + montantEligible);
      const ratio = montantEligible / baseTtc;
      const integral = Math.abs(Math.abs(montantEligible) - baseTtc) < 0.01;
      return {
        ...paiement,
        marge: Number(facture.marge) * ratio,
        caHT: Number(facture.caHT) * ratio,
        certification: integral ? facture.certification : 'À CERTIFIER — PRORATA ENCAISSEMENT',
        factureMarge: facture,
      };
    });
}

export function agregerMargeEncaissee(mouvements = [], debut = '', fin = '') {
  const selection = mouvements.filter((mouvement) => dansPeriode(mouvement, debut, fin));
  const incomplets = selection.filter((mouvement) => mouvement.marge == null || mouvement.caHT == null);
  const aCertifier = selection.some((mouvement) => texte(mouvement.certification).includes('CERTIFIER'));
  const disponible = selection.length > 0 && incomplets.length === 0;
  const marge = selection.reduce((total, mouvement) => total + Number(mouvement.marge || 0), 0);
  const caHT = selection.reduce((total, mouvement) => total + Number(mouvement.caHT || 0), 0);
  return {
    montant: disponible ? marge : null,
    caHT: disponible ? caHT : null,
    taux: disponible && Math.abs(caHT) > 0.000001 ? marge / caHT * 100 : null,
    statut: !disponible ? 'MARGE NON COLLECTÉE' : aCertifier ? 'À CERTIFIER' : 'CERTIFIÉ',
    mouvements: selection,
    nombreNonCollecte: incomplets.length,
  };
}
